import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { sendChatMessage, getChatHistory, clearChatHistory } from '@/services/leadChatService';
import type { ChatMessage, ChatContextData } from '@/types/leadChat';
import type { Lead } from '@/types/lead';
import { useToast } from '@/hooks/use-toast';

const getErrorMessage = (error: string): string => {
  if (error.includes('rate limit')) return 'Too many requests. Please wait a moment and try again.';
  if (error.includes('credits exhausted') || error.includes('402')) return 'OpenAI credits exhausted. Please contact your administrator.';
  if (error.includes('Unauthorized') || error.includes('401')) return 'Your session has expired. Please refresh the page and log in again.';
  if (error.includes('Lead not found') || error.includes('404')) return 'This lead could not be found in the database.';
  if (error.includes('Access denied') || error.includes('different company') || error.includes('403')) return "You don't have permission to access this lead.";
  return error || 'An unexpected error occurred. Please try again.';
};

export const useLeadChatbot = (lead: Lead) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isContextLoading, setIsContextLoading] = useState(false);
  const [contextData, setContextData] = useState<ChatContextData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hasLoadedContext, setHasLoadedContext] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: chatHistory } = useQuery({
    queryKey: ['leadChatHistory', lead.id],
    queryFn: () => getChatHistory(lead.id),
    enabled: isOpen,
  });

  useEffect(() => {
    if (chatHistory) setMessages(chatHistory);
  }, [chatHistory]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadContext = useCallback(async () => {
    if (!lead?.id) return;
    setIsContextLoading(true);
    setError(null);
    try {
      const context: ChatContextData = {
        leadName: lead.name,
        website: lead.website || undefined,
        category: lead.category || undefined,
        status: lead.status || undefined,
        hasWebSearchData: !!lead.webSearchData,
        hasNotes: (lead.notes?.length || 0) > 0,
        hasTasks: false,
        hasContacts: (lead.keyContacts?.length || 0) > 0,
        notesCount: lead.notes?.length || 0,
        tasksCount: 0,
        contactsCount: lead.keyContacts?.length || 0,
      };
      setContextData(context);
      setHasLoadedContext(true);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load lead context';
      setError(errorMsg);
      toast({ variant: 'destructive', title: 'Context Load Failed', description: errorMsg });
    } finally {
      setIsContextLoading(false);
    }
  }, [lead, toast]);

  useEffect(() => {
    if (isOpen && !hasLoadedContext) loadContext();
  }, [isOpen, hasLoadedContext, loadContext]);

  const sendMessage = useCallback(async (message: string, performWebSearch?: boolean) => {
    if (!message.trim() || isLoading) return;
    setError(null);
    setIsLoading(true);

    const tempUserMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      leadId: lead.id,
      userId: '',
      companyId: '',
      role: 'user',
      content: message,
      metadata: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setMessages(prev => [...prev, tempUserMessage]);

    try {
      const response = await sendChatMessage({ leadId: lead.id, message, performWebSearch });
      if (response.success && response.message) {
        setMessages(prev => {
          const withoutTemp = prev.filter(m => m.id !== tempUserMessage.id);
          const newMessages = [];
          if (response.userMessage) newMessages.push(response.userMessage);
          newMessages.push(response.message);
          return [...withoutTemp, ...newMessages];
        });
        queryClient.invalidateQueries({ queryKey: ['leadChatHistory', lead.id] });
      } else {
        setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
        const errorMsg = getErrorMessage(response.error || '');
        setError(errorMsg);
        toast({ variant: 'destructive', title: 'Failed to send message', description: errorMsg });
      }
    } catch {
      setError('An unexpected error occurred');
      setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
      toast({ variant: 'destructive', title: 'Error', description: 'An unexpected error occurred' });
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, lead.id, queryClient, toast]);

  const toggleChatOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearConversation = useCallback(async () => {
    try {
      const success = await clearChatHistory(lead.id);
      if (success) {
        setMessages([]);
        queryClient.invalidateQueries({ queryKey: ['leadChatHistory', lead.id] });
        toast({ title: 'Conversation cleared', description: 'Chat history has been deleted' });
      } else {
        toast({ variant: 'destructive', title: 'Failed to clear conversation', description: 'An error occurred' });
      }
    } catch {
      toast({ variant: 'destructive', title: 'Failed to clear conversation', description: 'An unexpected error occurred' });
    }
  }, [lead.id, queryClient, toast]);

  return {
    messages, isLoading, isContextLoading, contextData, error, isOpen,
    messagesEndRef, sendMessage, toggleChatOpen, clearError, clearConversation,
  };
};
